/* prettier-ignore */

import { Injectable, effect, inject, signal } from '@angular/core';
import { parseDates, areDatesValid } from './chat-actions/helpers/date.utils';
import { getCoordinates } from './chat-actions/helpers/google-location.utils';
import { RestService } from 'src/app/services/rest.service';
import { RegisterFormService } from './chat-actions/register-form.service';
import { ShowItemDetailsService } from './chat-actions/show-item-details.service';
import { ShowComponentService } from './chat-actions/show-component.service';
import { RemoveItemFromCustomRouteService } from './chat-actions/remove-item-from-custom-route.service';
import { AddItemToCustomRouteService } from './chat-actions/add-item-to-custom-route.service';
import { SendCurrentRouteService } from './chat-actions/send-current-route.service';
import { ShowTimelineOptions } from './chat-actions/show-timeline-options';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { isNotSuccess, validateRoute } from './chat-actions/guards/custom-route.guard';
import { PlannerDayStateService } from 'src/app/pages/custom-route/shared/planner/services';
import { from, mapTo, Observable, of, take, tap } from 'rxjs';
import { DataChatService } from './dataChat.service';
import { EditRouteService } from 'src/app/pages/custom-route/shared/planner/components/route-timeline/components/daily-route-list/services';
import { RegisterUserSubmissionService } from 'src/app/pages/register-user/services';
import { MatBottomSheetService, TranslationService } from 'src/app/services';
import { ModifyCustomRoute } from './chat-actions/modify-custom-route';
import { RestoreCustomRoute } from './chat-actions/restore-custom-route';
import { normalizeUtcDatesInDailyRoutes } from 'src/app/pages/custom-route/helpers/normalize-utc-dates';

@Injectable({ providedIn: 'root' })

export class ChatActionHandlerService {
    private _registerFormService = inject(RegisterFormService);
    private _registerUserSubmissionService = inject(RegisterUserSubmissionService);
    private _showItemDetails = inject(ShowItemDetailsService);
    private _showComponentService = inject(ShowComponentService);
    private _modifyItemToCustomRouteService = inject(ModifyCustomRoute);
    private _removeItemFromCustomRouteService = inject(RemoveItemFromCustomRouteService);
    private _addItemToCustomRouteService = inject(AddItemToCustomRouteService);
    private _restoreCustomRouteService = inject(RestoreCustomRoute);
    private _sendCurrentRouteService = inject(SendCurrentRouteService);
    private _showTimelineOptions = inject(ShowTimelineOptions)
    private _customRouteService = inject(CustomRouteService);
    private _editRouteService = inject(EditRouteService);
    private _plannerDayStateService = inject(PlannerDayStateService);
    private _dataChat = inject(DataChatService);
    private _restService = inject(RestService);
    private _translationService = inject(TranslationService);
    private _matBottomSheetService = inject(MatBottomSheetService);
    
    private _selectedLanguage = this._translationService.selectedLanguage;

    public openAIFeedback = signal(null);
    
    private _generatingRoute = signal(false);
    public generatingRoute = this._generatingRoute.asReadonly();

    // HRS-2384/B8: _generatingRoute is a duplicate-submission guard set when a generation
    // starts (detectFormChanges / checkValidForm), but it was only ever reset in
    // resetSession() — i.e. on the planner's ngOnDestroy. In the embed the planner stays
    // mounted across routes, so after the 1st route the flag stayed true and a 2nd "create
    // route" was dropped as "skipping duplicate" and never generated. The submission clears
    // DataChatService.generatingRoute once a generation settles; release the guard on that
    // falling edge so a new generation can run. Edge-tracked so a context that never sets
    // generatingRoute (no embed generation) doesn't get its guard spuriously released.
    private _prevGenerating = false;
    private _releaseGuardOnGenerationEnd = effect(() => {
        const generating = this._dataChat.generatingRoute();
        if (this._prevGenerating && !generating) this._generatingRoute.set(false);
        this._prevGenerating = generating;
    }, { allowSignalWrites: true });

    // HRS-2384/B8: detectFormChanges also reads _generatingRoute(), so releasing the guard
    // after a generation re-runs this effect; without a content check it would re-fire for
    // the same still-valid form → infinite regeneration loop. Dedupe the AUTO path by form
    // content (explicit re-generation goes through checkValidForm, which is user-driven and
    // intentionally not deduped).
    private _lastAutoGeneratedFormKey: string | null = null;

    detectFormChanges = effect(() => {
        // Read tracked signals up front so Angular registers both as dependencies
        // (radiusResolved must re-run this effect when the agent resolves the radius).
        const form = this._registerFormService.signalForm();
        const radiusResolved = this._registerFormService.radiusResolved();
        console.log("TOOLS:: Form values changed:", form);
        if (!this._registerFormService.isValidForm()) {
            const invalids = this._registerFormService.getInvalidControls();
            console.warn('TOOLS: Form invalid:', invalids);
            return;
        }

        // search_radius_km is optional (not part of validity) and is asked last. Hold
        // auto-generation until the agent has resolved it, so the route isn't generated
        // with a null radius before the answer lands.
        if (!radiusResolved) {
            console.log("TOOLS:: Form valid but waiting for search radius to be resolved before generating.");
            return;
        }

        console.log("TOOLS:: Form is valid, ready to generate route. Current values:", form);

        if (this._generatingRoute())return;

        // Skip if this exact form was already generated (startRoute records the key). Stops
        // the auto path from re-firing once the in-flight guard is released after completion.
        if (JSON.stringify(form) === this._lastAutoGeneratedFormKey) return;

        console.log("TOOLS:: SENDING ROUTE");
        this._generatingRoute.set(true);
        this.startRoute();
    }, { allowSignalWrites: true })

    public actionFromChatBot(result): Observable<void> {
        console.log("TOOLS::", result);
        switch (result?.action) {
        
            // CamelCase Mappings (Backend)
            //case 'CreateTravelDates': return this.setTravelDates(result);
            //case 'CreateTravelGroup': return this.setTravelGroupType(result);
            //case 'CreateTravelInterests': return this.setTravelInterests(result);
            ////case 'CreateTransportPreferences': return this.setTransportPreference(result);
            ////case 'CreateDistancePreferences': return this.setDistancePreference(result);
            //case 'CreateTravelTimes': return this.setTravelTimes(result);
            //case 'CreateStartingPoint': return this.createStartingPoint(result);
            //case 'CheckValidForm': return this.checkValidForm(result); // check valid form and start route generation
            //case 'ShowItemDetails': return this.showItemDetails(result);
            ////case 'ShowTimelineOptions': return this.showTimeLineOptions(result);
            ////case 'AddPointToItinerary': return this.addItem(result);
            //case 'RemovePointFromItinerary': return this.removeItem(result); // Guessing name
            //case 'ShowMap': return this.showMapView(result);

            // Legacy / snak_case mappings
            case 'create_travel_dates': return this.setTravelDates(result);
            case 'create_travel_group_type': return this.setTravelGroupType(result);
            case 'create_travel_interests': return this.setTravelInterests(result);
            case 'create_search_radius_preferences': return this.setSearchRadius(result);
            //case 'create_transport_preferences': return this.setTransportPreference(result);
            //case 'create_distance_preferences': return this.setDistancePreference(result);
            //case 'request_slot_candidates': return this.showTimeLineOptions(result);
            case 'restore_route': return this.restoreRoute(result);
            case 'modify_route': return this._modifyItem(result);
            case 'show_timeline_options': return this.showTimeLineOptions(result);
            case 'show_point_details': return this.showItemDetails(result);
            //case 'add_point_to_itinerary': return this.addItem(result); // TODO: REMOVED -> REPLACE BY MODIFY
            //case 'remove_point_from_itinerary': return this.removeItem(result);
            case 'change_language': return this.changeLanguage(result);
            case 'return_to_timeline': return this.showTimeLineView(result); // TODO: FORGET FOR NOW
            case 'return_to_map': return this.showMapView(result); // TODO: FORGET FOR NOW
            case 'go_to_mail_form': return this.showSendRouteView(result); // TODO: FORGET FOR NOW
            //case 'get_current_itinerary': return this.sendCurrentRoute(result);
            case 'show_route_day': return this.showRouteDay(result);
            case 'create_starting_point': return this.createStartingPoint(result); // * IGNORE
            //case 'modify_timeline_slot': return this.modifyTimelineSlot(result);
            case 'check_valid_form': return this.checkValidForm(result); // check valid form and start route generation
            case 'reload_route': return this.restoreRoute(result); // TODO: DELETE
            case 'update_interests': return this.updateInterests(result); // * IGNORE
            default:
                console.warn("TOOLS:: Unknown action:", result?.action);
                return of(void 0);
        }
    }

    private generateInitialPayload(result: any) {
        return {
            action: result.action,
            content: null,
            is_successful: false,
            operation_id: result.operation_id,
            completion_id: result.completion_id,
        }
    }

    private changeLanguage(item): Observable<void> {
        console.warn("change_language tool not implemented");
        const feedBack = this.generateInitialPayload(item);
        this.openAIFeedback.set(feedBack);
        return of(void 0);
    }
    
    public resetSession(): void {
      this._generatingRoute.set(false);
    }

    //---------- IA REGISTER USER TO GENERATE ROUTE ----------------------------------------
    private setTravelDates(item: any): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const dates = parseDates(item.start_date, item.end_date);
        if (!areDatesValid(dates)) {
            this.openAIFeedback.set({ ...feedBack, reason: 'invalid dates' });
            return of(void 0);
        }

        this._registerFormService.setDateRange(item);
        this.openAIFeedback.set({ ...feedBack, is_successful: true });
        return of(void 0);
    }

    private setTravelGroupType(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._registerFormService.setTripType(item.group_id);
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private setTravelInterests(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._registerFormService.setInterestIdList(item.interest_ids)
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private setTravelTimes(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._registerFormService.setStartTime(item.start_time);
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private setSearchRadius(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        // NOTE: arg key `search_radius_km` must match the backend tool contract.
        // null ("sin límite") is intentionally omitted from the payload by the adapter.
        const res = this._registerFormService.setSearchRadius(item.search_radius_km ?? null);
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private createStartingPoint(args: any): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        // Priorizar la ciudad configurada en el dispositivo (restService)
        const city = this._dataChat.geocodingCity() ||
            this._restService.device()?.location?.city ||
            this._restService.device()?.location?.address ||
            this._restService?.subDomain || null;

        console.log(`TOOLS: Geocoding '${args?.starting_point}' with context city: ${city}`);

        return from(getCoordinates(args?.starting_point, city)).pipe(
            tap(startLocation => {
                if (!startLocation) {
                    this.openAIFeedback.set({
                        ...feedBack,
                        is_successful: false,
                        reason: 'No se ha podido establecer el punto de partida'
                    });
                    return;
                }
                const address = {
                    ...startLocation,
                    title: args?.starting_point
                };
                console.log("TOOLS:: Geocoding result:", address);
                const res = this._registerFormService.setStartLocation(address);
                this.openAIFeedback.set({ ...feedBack, ...res });
            }),
            take(1),
            mapTo(void 0)
        );
    }

    private startRoute(): void {
      // Record the form snapshot that this generation covers so the auto-trigger
      // (detectFormChanges) won't regenerate the same form after the guard releases
      // (HRS-2384/B8). Keyed off signalForm() — the same source detectFormChanges checks.
      this._lastAutoGeneratedFormKey = JSON.stringify(this._registerFormService.signalForm());
      const values = this._registerFormService.getFormValues();
      this._registerUserSubmissionService.handleRegisterUserFormSubmission(values);
    }
    
    //---------- IA CHATBOT PLANNER V3 FUNCTIONS ----------------------------------------
    private showItemDetails(item: any): Observable<void> {
      const feedBack = this.generateInitialPayload(item);

      // Si no tiene element_id pero tiene found_name, resolver desde la ruta actual
      if (!item?.element_id && item?.found_name) {
        const resolved = this._resolvePointByName(item.found_name);
        if (resolved) {
          item = { ...item, element_id: String(resolved.id), type: resolved.type || 'point' };
        } else {
          this.openAIFeedback.set({ ...feedBack, is_successful: false, reason: 'No se encontró el punto en la ruta' });
          return of(void 0);
        }
      }

      return this._showItemDetails.show(item).pipe(
        take(1),
        tap(res => this.openAIFeedback.set({ ...feedBack, ...res })),
        mapTo(void 0)
      );
    }
    
    private _modifyItem(args: any): Observable<void> {
      const feedBack = this.generateInitialPayload(args);
      return this._modifyItemToCustomRouteService.modifyItem(args).pipe(
        take(1),
        tap(res => {
          if (res?.is_successful) {
            const body = {
              ...res.sync_payload,
              dailyRoutes: normalizeUtcDatesInDailyRoutes(res.sync_payload.dailyRoutes),
            };
            this._editRouteService.syncEditedCustomRouteDataWithPythonBackendServiceSignal(body);
      
            this._matBottomSheetService.closeAllMatBottomSheets();
            
            this.openAIFeedback.set({ ...feedBack, ...res, is_successful: res?.is_successful });
          } else {
            this.openAIFeedback.set({ ...feedBack, is_successful: false, reason: this._editRouteService.errorEditedCustomRouteData() });
          }
        }),
        mapTo(void 0)
      );
    }

    private addItem(args: any): Observable<void> {
      const feedBack = this.generateInitialPayload(args);
      return this._addItemToCustomRouteService.addItem(args).pipe(
        take(1),
        tap((res: any) => {
          const body = {
            ...res.sync_payload,
            dailyRoutes: normalizeUtcDatesInDailyRoutes(res.sync_payload.dailyRoutes),
          };
          this._editRouteService.syncEditedCustomRouteDataWithPythonBackendServiceSignal(body);
          this.openAIFeedback.set({ ...feedBack, is_successful: true });
        }),
        mapTo(void 0)
      );
    }

    private removeItem(args): Observable<void> {
      const feedBack = this.generateInitialPayload(args);
      return this._removeItemFromCustomRouteService.removeItem(args).pipe(
        take(1),
        tap(res => {
          if (res?.is_successful) {
            const body = {
              ...res.sync_payload,
              dailyRoutes: normalizeUtcDatesInDailyRoutes(res.sync_payload.dailyRoutes),
            };
            this._editRouteService.syncEditedCustomRouteDataWithPythonBackendServiceSignal(body);
          }
          this.openAIFeedback.set({ ...feedBack, ...res, is_successful: res?.is_successful });
        }),
        mapTo(void 0)
      );
    }
    
    private restoreRoute(item: any): Observable<void> {
      const feedBack = this.generateInitialPayload(item);

      return this._restoreCustomRouteService.restoreRoute().pipe(
        take(1),
        tap(res => {
          if (res?.is_successful) {
            this._editRouteService.restoreEditedCustomRouteDataWithPythonBackend();
            this.openAIFeedback.set({ ...feedBack, ...res, is_successful: res?.is_successful });
          } else {
            this.openAIFeedback.set(
              { ...feedBack, 
                is_successful: false, 
                reason: this._editRouteService.errorEditedCustomRouteData() 
              }
            );
          }
        }),
        mapTo(void 0)
      );
    }
    
    private showTimeLineOptions(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const dayIndex = Number(item?.dayIndex ?? 0);
        const slotHour = Number(item?.slotHour ?? 0);
        const res = this._showTimelineOptions.triggerSlotClick(dayIndex, slotHour);
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private showTimeLineView(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._showComponentService.showTimelineComponent();
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private showMapView(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._showComponentService.showMapComponent();

        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private showSendRouteView(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        const res = this._showComponentService.showSendRouteComponent();
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private sendCurrentRoute(args): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        const res = this._sendCurrentRouteService.getRoute();
        
        console.log("TOOLS:: sendCurrentRoute result:", res);
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    private showRouteDay(args): Observable<void> {
        const feedBack = this.generateInitialPayload(args);
        const route = this._editRouteService.editedCustomRouteData();
        const token = this._customRouteService.token();

        const ctx = validateRoute(route, token, args?.day);
        if (isNotSuccess(ctx)) {
            this.openAIFeedback.set({ ...feedBack, ctx });
            return of(void 0);
        }

        const dayIndex = Number(args?.day - 1);
        this._plannerDayStateService.setDayToShowIndex(dayIndex);
        this._showComponentService.showTimelineComponent();
        this.openAIFeedback.set({ ...feedBack, is_successful: true });
        return of(void 0);
    }

    /** Normaliza texto: minúsculas + sin acentos */
    private _normalize(text: string): string {
        return (text || '').toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    /** Busca un punto en la ruta actual por nombre (fuzzy match, sin acentos) */
    private _resolvePointByName(name: string): any | null {
        const route = this._editRouteService.editedCustomRouteData();
        if (!route?.dailyRoutes) return null;

        const nameNorm = this._normalize(name);
        for (const dailyRoute of route.dailyRoutes) {
            if (!dailyRoute?.points) continue;
            for (const point of dailyRoute.points) {
                const title = this._normalize(point.title?.[this._selectedLanguage()] ?? '');
                if (title === nameNorm || title.includes(nameNorm) || nameNorm.includes(title)) {
                    return point;
                }
            }
        }
        return null;
    }

    private checkValidForm(item): Observable<void> {
        const feedBack = this.generateInitialPayload(item);
        if (!this._registerFormService.isValidForm()) {
            const invalidControls = this._registerFormService.getInvalidControls()?.map(e => e.controlName) ?? [];
            const res = {
                reason: `Se necesitan los siguientes inputs: ${invalidControls.join(',')}`
            };
            this.openAIFeedback.set({ ...feedBack, ...res });
            // Red de seguridad: el asistente intenta generar con el formulario
            // incompleto → en vez de quedarse mudo, decirle al USUARIO qué falta.
            this._notifyMissingFormFields(invalidControls);
            return of(void 0);
        }

        // check_valid_form es una orden EXPLÍCITA del asistente para generar la ruta
        // (p.ej. tras confirmar un "crear otra ruta" con nuevos datos). Solo debemos
        // saltar cuando YA hay una generación realmente en curso PARA ESTE MISMO
        // formulario: detectFormChanges registra la clave del formulario al disparar
        // startRoute(), así que "guard activo + clave == snapshot actual" = generación en
        // vuelo real (evita el doble disparo cuando el path automático acaba de arrancar).
        // Si el guard quedó activo por una generación ANTERIOR o intermedia (otro
        // formulario) que aún no se liberó, está OBSOLETO respecto al formulario final del
        // reemplazo y NO debe descartar esta 2ª generación (HRS-2582; misma clase de bug
        // que HRS-2384/B8, variante confirmar-reemplazo).
        const currentFormKey = JSON.stringify(this._registerFormService.signalForm());
        const generationInFlightForThisForm =
            this._generatingRoute() && this._lastAutoGeneratedFormKey === currentFormKey;

        if (generationInFlightForThisForm) {
            console.log("TOOLS:: CheckValidForm OK but route already generating for this form, skipping duplicate startRoute()");
            const res = { is_successful: true };
            this.openAIFeedback.set({ ...feedBack, ...res });
            return of(void 0);
        }

        // Guard obsoleto (activo pero de un formulario distinto / sin generación real para
        // el snapshot actual): límpialo junto con el dedup del path automático para que la
        // orden explícita pueda regenerar. startRoute() vuelve a registrar la clave del
        // formulario actual, así que el path automático no re-disparará para este mismo
        // snapshot.
        this._generatingRoute.set(false);
        this._lastAutoGeneratedFormKey = null;

        console.log("TOOLS:: CheckValidForm OK -> Starting route generation");
        this._generatingRoute.set(true);
        this.startRoute();

        const res = { is_successful: true };
        this.openAIFeedback.set({ ...feedBack, ...res });
        return of(void 0);
    }

    /**
     * Red de seguridad (front): si el asistente pide generar la ruta con el
     * formulario incompleto, mostramos en el CHAT qué falta —en lenguaje claro—
     * en vez de que el widget se quede mudo sin generar nada. Lo correcto sería
     * que el prompt del asistente complete los campos; esto solo evita el "no
     * pasa nada" y guía al usuario.
     */
    private _notifyMissingFormFields(invalidControls: string[]): void {
        const labels: Record<string, string> = {
            startDate: 'las fechas del viaje',
            endDate: 'las fechas del viaje',
            startLocationAddress: 'el punto de inicio (una dirección o lugar concreto)',
            tripType: 'con quién viajas',
            categories: 'al menos un interés (cultura, gastronomía, naturaleza…)',
        };

        // subdomain es interno (config), no se le pide al usuario.
        const missing = Array.from(
            new Set(invalidControls.map((c) => labels[c]).filter(Boolean))
        );
        if (missing.length === 0) return;

        const list = missing.length === 1
            ? missing[0]
            : `${missing.slice(0, -1).join(', ')} y ${missing[missing.length - 1]}`;
        const content = `Para preparar tu ruta todavía me falta saber: ${list}. ¿Me lo dices y la genero?`;

        // Evitar repetir el mismo aviso si ya es el último mensaje.
        const current = this._dataChat.messages();
        const last = current[current.length - 1];
        if (last?.tags?.includes('form-missing') && last?.content === content) return;

        this._dataChat.messages.update((prev) => [
            ...prev,
            { id: `form-missing-${prev.length}`, role: 'assistant', content, show: true, tags: ['form-missing'] },
        ]);
    }

    private updateInterests(result: any): Observable<void> {
        const tags = result?.tags || [];
        console.log("TOOLS:: update_interests tags:", tags);

        const messages = this._dataChat.messages();

        // Buscamos el último mensaje del asistente para ponerle los tags
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role === 'assistant') {
                const updatedMessages = [...messages];
                updatedMessages[i] = { ...updatedMessages[i], tags };
                this._dataChat.messages.set(updatedMessages);
                break;
            }
        }

        return of(void 0);
    }

}